import React from "react";
import Feature from "./Template/Feature";

const ProductResearch = () => {
    const data = {
        title: "Walmart Product Research Tool",
        subtitle:
            "Decide whether a Walmart product is worth sourcing — profit after fees, demand, competition, and gating — without leaving the listing.",
        date: "2026-07-13",

        quickAnswer: (
            <>
                Marter is a Walmart product research tool. On any Walmart product page — or from a barcode scan in
                the mobile app — it answers the four questions that decide a sourcing call: what's my net profit
                after Walmart's fees, how many units does this actually sell, how many sellers am I fighting, and
                am I even allowed to list it. One plan, $24/month, every feature included.
            </>
        ),

        intro: (
            <>
                Product research fails in one of two ways. Either you never do it — you buy on gut and find out at
                settlement — or you do it so thoroughly that you research four products an hour and never build a
                catalog. The fix isn't more data. It's having the deciding data arrive at the moment of the
                decision.
            </>
        ),

        benefits: [
            {
                title: "Net profit, not revenue",
                body:
                    "Referral fees, fulfillment cost, and your cost of goods resolved into a real margin. Revenue is a vanity number; margin is the decision."
            },
            {
                title: "Sales estimations",
                body:
                    "Estimated monthly units on the listing, so demand is a number instead of a hunch."
            },
            {
                title: "Competition depth",
                body:
                    "How many sellers share the listing, where they're priced, and how much stock they're sitting on."
            },
            {
                title: "Historical context",
                body:
                    "Price and offer-count history, so you can tell a durable listing from one that's already being raced to the floor."
            },
            {
                title: "Gating and restrictions",
                body:
                    "Find out that a product is restricted for your account before the pallet arrives, not after."
            },
            {
                title: "Works in-store too",
                body:
                    "Barcode scan in the Marter mobile app runs the identical analysis on a clearance shelf."
            }
        ],

        howItWorks: [
            "Install the Marter Chrome extension, or open the Marter mobile app to scan a barcode in-store.",
            "Open a Walmart product page — Marter's panel loads with the listing.",
            "Enter your cost of goods. Marter returns net profit after Walmart fees, demand estimate, seller count, stock, price history, and gating status.",
            "Buy it, skip it, or set an alert and move to the next product."
        ],

        sections: [
            {
                id: "criteria",
                title: "The four questions a sourcing decision actually turns on",
                content: (
                    <>
                        <strong>1. Does it make money?</strong> Not "is the spread big" — what is the margin after
                        the referral fee, fulfillment, and your landed cost. Marter computes it on the page.
                        <br />
                        <br />
                        <strong>2. Does it sell?</strong> A 40% margin on a product that moves three units a month
                        is a storage bill. Sales estimations turn demand into a number you can multiply.
                        <br />
                        <br />
                        <strong>3. Who else is selling it?</strong> Offer count and seller pricing tell you whether
                        the margin is yours or shared with eleven other people about to undercut you.
                        <br />
                        <br />
                        <strong>4. Can you even list it?</strong> Gated and restricted categories are the most
                        expensive thing to learn late. Marter checks gating before you buy.
                    </>
                )
            },
            {
                id: "seller-center",
                title: "Why Walmart Seller Center can't do this",
                content: (
                    <>
                        Seller Center reports on the catalog you already have. It has nothing to say about a product
                        you're considering — no profit projection, no demand estimate for someone else's listing, no
                        competitive depth, no gating pre-check. Product research is inherently about items you don't
                        sell yet, which is precisely the blind spot in Walmart's own tooling. That gap is what Marter
                        was built to fill.
                    </>
                )
            },
            {
                id: "amazon-sellers",
                title: "If you're coming from Amazon",
                content: (
                    <>
                        Amazon sellers arrive with a workflow shaped by Keepa and a scanner app, and correctly
                        assume Walmart has an equivalent. It doesn't — Keepa is Amazon-only. Marter is the tool
                        that reconstructs the workflow on Walmart: overlay on the product page, profit math,
                        demand, competition, history, gating, plus a barcode scanner for in-store sourcing. And at
                        $24/month it costs less than Keepa's €29/month.
                    </>
                )
            }
        ],

        faqs: [
            {
                question: "What is the best product research tool for Walmart?",
                answer:
                    "Marter is built specifically for Walmart Marketplace product research. It shows net profit after Walmart fees, sales estimations, competing sellers and their pricing, price and offer history, stock levels, and gating status directly on the Walmart product page. It's $24/month on a single plan with every feature included."
            },
            {
                question: "How do I do product research for Walmart Marketplace?",
                answer:
                    "Work from the listing outward. For each candidate product, establish net margin after referral and fulfillment fees, estimated monthly sales, how many sellers share the listing and at what prices, how price and offer count have trended, and whether the category is gated for your account. Marter answers all five on the product page so the loop takes seconds rather than tabs."
            },
            {
                question: "Can I do Walmart product research for free?",
                answer:
                    "You can approximate it by hand — Walmart's fee schedule, manual seller counts, and guesses at demand. It works for a handful of products and collapses beyond that, because the data that matters most (sales estimates, offer history, gating status) is not published on the listing."
            },
            {
                question: "Does Marter work for retail arbitrage and online arbitrage?",
                answer:
                    "Yes. The Chrome extension covers online sourcing on Walmart product pages, and the mobile app runs the same profit, demand, competition, and gating analysis from a barcode scan for in-store retail arbitrage. Both are included in the $24/month plan."
            },
            {
                question: "How much does Marter cost?",
                answer:
                    "$24 per month, one plan, all features — extension, mobile app, product research, price and offer history, sales estimations, alerts, and gating checks. No tiers, no per-feature upsells."
            }
        ],

        relatedFeatures: [
            { title: "Walmart Seller Chrome Extension", url: "/features/chrome-extension" },
            { title: "Walmart Price Tracker", url: "/features/price-tracker" }
        ],

        relatedPosts: [
            { title: "How to Do Product Research for Walmart Marketplace", url: "/blogs/how-to-do-product-research-for-walmart-marketplace" },
            { title: "How to Find Profitable Products", url: "/blogs/how-to-find-profitable-products" },
            { title: "How to Avoid Restricted or Gated Products on Walmart", url: "/blogs/how-to-avoid-restricted-or-gated-products-on-walmart" }
        ],

        ctaHeading: "Research the product where the product is",
        ctaBody:
            "Profit, demand, competition, and gating on the Walmart listing itself. $24/month, single plan, everything included.",

        meta: {
            description:
                "Walmart product research tool: net profit after fees, sales estimations, competing sellers, price history, and gating checks on every Walmart product page. $24/month, single plan.",
            canonical: "https://www.getmarter.com/features/product-research",
            keywords:
                "walmart product research tool, walmart product research, product research for walmart, walmart sales estimator, walmart profit calculator",
            dateModified: "2026-07-13"
        }
    };

    return <Feature {...data} />;
};

export default ProductResearch;
